import React, { VFC } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from '../../contexts/translationContext';
import { theme } from '../../utils/theme';
import { IconVariants } from '../atoms/icons/types';

import SvgIcon from '../atoms/SvgIcon';
import Typography, { ITranslation } from '../atoms/Typography';
import Spacing from '../atoms/Spacing';

interface Props extends React.HTMLProps<HTMLInputElement>, ITranslation {
  title?: string;
  active?: boolean;
  borderColor?: string;
  mandatory?: boolean;
  type?: string;
  error?: string;
  placeholder?: string;
  placeholderParams?: string;
  icon?: IconVariants;
  accept?: string;
  updateFile: (file: File) => void;
  file: File | undefined;
}

interface IStyledFileInput
  extends React.HTMLProps<HTMLInputElement | HTMLDivElement>,
    ITranslation {
  borderColor?: string;
  disabled?: boolean;
  error?: string;
}

const InputWrapper = styled.div`
  width: 100%;
`;

const StyledFileInputContainer = styled.div<IStyledFileInput>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${theme.colors.grey[0]};
  padding: 2rem 10rem;
  border: 3px dashed ${theme.colors.grey[50]};
  ${({ error }) =>
    error &&
    css`
      border: 1px solid ${theme.colors.red[100]};
    `}}
  :disabled {
    color: ${theme.colors.grey[20]};
    outline: none;
  }
`;

const StyledFileInput = styled.input<IStyledFileInput>`
  display: block;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0;
  cursor: pointer;
`;

const StyledErrorMsg = styled(Typography)`
  padding: 0 0 0 0.8rem;
  margin-top: 0.4rem;
  color: ${theme.colors.red[100]};
`;

const FileInput: VFC<Props> = ({
  value,
  title,
  placeholder,
  borderColor,
  disabled,
  type = 'file',
  mandatory = false,
  error,
  icon,
  accept,
  updateFile,
  file,
}) => {
  const { translate } = useTranslation();
  const translatedPlaceholder = placeholder && translate(placeholder, null);
  return (
    <InputWrapper>
      <Typography translateKey={title} variant="input-label">
        {mandatory ? `${title} *` : title}
      </Typography>
      <StyledFileInputContainer error={error}>
        <StyledFileInput
          value={value}
          type={type}
          disabled={disabled}
          borderColor={borderColor}
          placeholder={translatedPlaceholder}
          onChange={(e) => {
            updateFile(e.target.files[0]);
          }}
          error={error}
          accept={accept}
        />
        <SvgIcon
          color={theme.colors.grey[100]}
          icon={icon}
          iconSize="xLarge"
        />
        <Spacing spacing={2} />
        <Typography variant="p-semi-bold" translateKey="select_file" />
        <Typography variant="p" color={theme.colors.grey[50]}>{file?.name}</Typography>
      </StyledFileInputContainer>

      {!!error && (
        <StyledErrorMsg variant="input-label">{error}</StyledErrorMsg>
      )}
    </InputWrapper>
  );
};

export default FileInput;
